import React, { useState } from "react";
import * as XLSX from "xlsx";

const QuizApp = () => {
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [score, setScore] = useState({ correct: 0, incorrect: 0 });
  const [showAnswer, setShowAnswer] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [questionCount, setQuestionCount] = useState(10);
  const [wrongAnswers, setWrongAnswers] = useState([]);
  const [originalPool, setOriginalPool] = useState([]);
  const [usedQuestions, setUsedQuestions] = useState([]);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        let formattedQuestions = jsonData.slice(1).map((row, index) => ({
          id: index + 1,
          title: row[0],
          selection: [row[2], row[3], row[4], row[5]],
          answer: row[1] - 1,
          explanation: row[6] || "（此題暫無解題說明）",
        }));

        formattedQuestions = shuffleArray(formattedQuestions);

        setOriginalPool(formattedQuestions);
        const initialQuestions = formattedQuestions.slice(0, questionCount);
        setQuestions(initialQuestions);
        setUsedQuestions(initialQuestions.map(q => q.id));
        setCurrentQuestion(0);
        setScore({ correct: 0, incorrect: 0 });
        setShowAnswer(false);
        setQuizCompleted(false);
        setWrongAnswers([]);
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const shuffleArray = (array) => array.sort(() => Math.random() - 0.5);

  const handleAnswerSelection = (index) => {
    if (selectedAnswer === null) {
      setSelectedAnswer(index);
      setShowAnswer(true);
      const correct = index === questions[currentQuestion].answer;
      setScore((prev) => ({
        correct: prev.correct + (correct ? 1 : 0),
        incorrect: prev.incorrect + (correct ? 0 : 1),
      }));
      if (!correct) {
        setWrongAnswers((prev) => [...prev, questions[currentQuestion]]);
      }
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setShowAnswer(false);
    } else {
      setQuizCompleted(true);
    }
  };

  const handleRestart = () => {
    setQuestions([]);
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setScore({ correct: 0, incorrect: 0 });
    setShowAnswer(false);
    setQuizCompleted(false);
    setWrongAnswers([]);
    setUsedQuestions([]);
  };

  const handleContinuePractice = () => {
    const unusedQuestions = originalPool.filter(q => !usedQuestions.includes(q.id));
    const newSet = shuffleArray(unusedQuestions).slice(0, Math.min(questionCount, unusedQuestions.length));
    setQuestions(newSet);
    setUsedQuestions(prev => [...prev, ...newSet.map(q => q.id)]);
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setScore({ correct: 0, incorrect: 0 });
    setShowAnswer(false);
    setQuizCompleted(false);
    setWrongAnswers([]);
  };

  return (
    <div style={{ textAlign: "center", padding: "20px" }}>
      <h1>刷題練習</h1>
      <input type="file" accept=".xlsx, .xls" onChange={handleFileUpload} />
      <input
        type="number"
        min="1"
        max="100"
        value={questionCount}
        onChange={(e) => setQuestionCount(parseInt(e.target.value) || 10)}
        style={{ marginLeft: "10px", width: "60px" }}
      /> 題

      {questions.length > 0 && !quizCompleted && (
        <div>
          <h2>{questions[currentQuestion].title}</h2>
          <ul style={{ listStyle: "none", padding: 0, textAlign: "left", display: "inline-block", fontSize: "20px" }}>
            {questions[currentQuestion].selection.map((option, index) => {
              const isCorrect = index === questions[currentQuestion].answer;
              const isSelected = index === selectedAnswer;

              let prefix = "";
              let background = "white";
              if (selectedAnswer !== null) {
                if (isCorrect) {
                  prefix = "✔️ ";
                  background = "lightgreen";
                } else if (isSelected) {
                  prefix = "❌ ";
                  background = "salmon";
                }
              }

              return (
                <li
                  key={index}
                  style={{ padding: "10px", margin: "5px 0", background, cursor: selectedAnswer === null ? "pointer" : "default" }}
                  onClick={() => handleAnswerSelection(index)}
                >
                  {prefix + option}
                </li>
              );
            })}
          </ul>

          {showAnswer && (
            <div>
              <button onClick={handleNextQuestion} style={{ padding: "10px", marginTop: "20px" }}>
                {currentQuestion < questions.length - 1 ? "下一題" : "結束測驗"}
              </button>
              <p style={{ marginTop: "20px", color: "#333", fontSize: "18px", maxWidth: "600px", marginLeft: "auto", marginRight: "auto" }}>
                <strong>解題思維：</strong><br />
                {questions[currentQuestion].explanation}
              </p>
            </div>
          )}

          <p>進度: {currentQuestion + 1} / {questions.length}</p>
        </div>
      )}

      {quizCompleted && (
        <div>
          <h2>測驗結束</h2>
          <p>答對題數: {score.correct}</p>
          <p>答錯題數: {score.incorrect}</p>

          {wrongAnswers.length > 0 && (
            <div style={{ textAlign: "left", margin: "20px auto", width: "80%" }}>
              <h3>📘 答錯題本：</h3>
              {wrongAnswers.map((q, idx) => (
                <div key={idx} style={{ marginBottom: "15px", padding: "10px", background: "#f9f9f9", border: "1px solid #ddd" }}>
                  <strong>題目：</strong>{q.title}<br />
                  <strong>正確答案：</strong>{q.selection[q.answer]}<br />
                  <strong>解題思維：</strong>{q.explanation}
                </div>
              ))}
            </div>
          )}

          <button onClick={handleContinuePractice} style={{ padding: "10px", margin: "10px" }}>在同一個檔案繼續練習</button>
          <button onClick={handleRestart} style={{ padding: "10px", margin: "10px" }}>重新上傳練習</button>
        </div>
      )}
    </div>
  );
};

export default QuizApp;
